
import {
  computed, defineComponent, onMounted, onUnmounted, reactive, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { descriptionWithLink } from '@/helpers/utils';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMainStore } from '@/store/mainStore';
import AppHeader from './AppHeader.vue';
import DrivePhotoCarousel from '@/modules/drive/components/DrivePhotoCarousel.vue';
import {
  CASE_VIEW_NAVIGATION, NAVIGATION_PAGES, PHOTO_VIEW_TYPE, SHARE_SETTINGS, SHARE_TYPES,
} from '@/types/enums';
import DriveOverview from '@/modules/drive/components/ViewSubpages/DriveOverviewSubpage.vue';
import DriveFiles from '@/modules/drive/components/ViewSubpages/DriveFilesSubpage.vue';
import CaseSettingsPopUp from '@/components/BottomPopUp/PopUpTypes/Settings/CaseSettingsPopUp.vue';
import MoveToPopUp from '@/components/BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
import SharePopUp from '@/components/BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import {
  ICase, IFolder, IMedia, IPhoto, IReactiveSharePopUp, ISharedLable,
} from '@/types/interfaces';
import DriveSharedBar from '@/modules/drive/components/DriveSharedBar.vue';
import AppSkeletonCaseView from '@/components/skeletons/AppSkeletonCaseView.vue';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import DriveTooltip from '@/modules/drive/components/UI/DriveTooltip.vue';
import DriveCasePhotos from '@/modules/drive/components/desktop/cases/DriveCasePhotos.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import AppHeaderUploadProgress from '@/components/AppHeaderUploadProgress.vue';

export default defineComponent({
  emits: ['cancel', 'done'],
  props: {
    title: { type: String, default: 'Case' },
    photoSkeletons: { type: Number, default: 0 },
    fileSkeletons: { type: Number, default: 0 },
    caseId: { type: String },
    selector: { type: Boolean, default: false },
    iCase: { type: Object as () => ICase },
  },
  components: {
    AppHeader,
    DriveSharedBar,
    DrivePhotoCarousel,
    DriveOverview,
    DriveFiles,
    CaseSettingsPopUp,
    MoveToPopUp,
    SharePopUp,
    AppSkeletonCaseView,
    DriveCasePhotos,
    DriveTooltip,
    AppHeaderUploadProgress,
  },
  setup(props, { emit }) {
    const messagesStore = useMessagesStore();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const route = useRoute();
    const router = useRouter();
    const currentSubpage = ref<CASE_VIEW_NAVIGATION>(CASE_VIEW_NAVIGATION.OVERVIEW);
    const positionData = ref({});

    const isNetwork = computed(() => route.meta.isNetwork);
    const isCasePage = computed(() => route.meta.navigationPage === NAVIGATION_PAGES.CASE_VIEW);
    const isMy = computed(() => driveStore.currentCase?.owner?.accountId === mainStore.user.accountId);
    const toShare = computed(() => (!props.selector && isMy.value)
      || (driveStore.currentCase.root && driveStore.currentCase.shareSettings?.includes(SHARE_SETTINGS.RESHARE_ALLOWED)));
    const allowSave = computed(() => (!props.selector && isMy.value)
      || (driveStore.currentCase.root && driveStore.currentCase.shareSettings?.includes(SHARE_SETTINGS.ALLOW_SAVE)));

    const caseLoading = ref(true);
    const isCaseCopying = ref(false);
    const caseCopyTitle = ref('');
    const createdDate = computed(() => new Date(driveStore.currentCase.created as any).toLocaleDateString('en-US', {
      month: 'long', day: '2-digit', year: 'numeric',
    }));

    const updatedDate = computed(() => new Date(driveStore.currentCase.updated as any).toLocaleDateString('en-US', {
      month: 'long', day: '2-digit', year: 'numeric',
    }));

    const fullName = computed(() => `${driveStore.currentCase.owner.firstName} ${driveStore.currentCase.owner.lastName}`);
    const isMoveToCasePopUp = ref(false);
    const isCaseSettingsPopUp = ref(false);
    const sharedPopUp: IReactiveSharePopUp = reactive({
      item: null,
      type: null,
    });

    const bufferCase = ref<ICase>();
    const bufferPhotoList = ref<IMedia[]>([]);

    function selectPhoto(photo: IMedia): void {
      driveStore.appPhotoViewMedia = photo;
    }

    function selectVideo(video: IMedia): void {
      driveStore.appPhotoViewMedia = video;
    }

    function doneAction() {
      emit('done', driveStore.currentCase.id);
    }

    function fetchCase() {
      if (props.iCase) {
        driveStore.currentCase = { ...props.iCase };
        driveStore.photoList = props.iCase.photos;
        caseLoading.value = false;
        return;
      }
      driveStore.getCaseById(props.caseId as string, isNetwork.value)
        .then((res) => {
          driveStore.currentCase = { ...res };
          driveStore.photoList = driveStore.currentCase.photos;
        })
        .finally(() => {
          caseLoading.value = false;
        });
    }

    function openMovePopUp() {
      isCaseSettingsPopUp.value = false;
      isMoveToCasePopUp.value = true;
    }
    function viewAllCases() {
      if (isNetwork.value) {
        router.push('/drive/network/cases');
      } else {
        router.push('/drive/my/cases');
      }
    }
    function viewNetworkCases() {
      router.push('/drive/network/cases');
    }
    function deleteCase() {
      const isShared = !!driveStore.currentCase.share;
      const currentConv = !!messagesStore.currentConversation;

      if (isShared && currentConv) {
        const label: ISharedLable = {
          name: '',
          photo: {} as IPhoto,
          isGroup: messagesStore.currentConversation!.info.isGroup,
          id: messagesStore.currentConversation!.info.id,
        };

        driveStore.removeSharingInfo(driveStore.currentCase, label);
      } else {
        driveStore.deleteCase();
      }

      isCaseSettingsPopUp.value = false;
      emit('cancel');
    }
    const photoUrl = computed(() => {
      if (driveStore.currentCase.owner.photo?.url) {
        return lowResUrlByViewTypeByUrl(driveStore.currentCase.owner.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });
    function updateLocation(folderId: string | null) {
      driveStore.currentCase!.folderId = folderId;
    }

    function openSharePopUp(item: IMedia | IFolder | ICase, type: SHARE_TYPES) {
      sharedPopUp.item = item;
      sharedPopUp.type = type;
    }

    function closeSharePopUp() {
      sharedPopUp.item = null;
      sharedPopUp.type = null;
    }

    function removeSharedItemFromStore() {
      closeSharePopUp();
      driveStore.fullscreenFile = undefined;
      driveStore.fullscreenPhoto = undefined;
      driveStore.appPhotoViewMedia = undefined;
    }

    function setSharedLabels(labels: ISharedLable[]) {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // sharedPopUp.item!.sharedLabels = labels;
      if (labels) {
        //
      }
    }
    function viewProfile(): void {
      if (mainStore.user.accountId === driveStore.currentCase.owner.accountId) {
        return;
      }
      router.push(`/profile/${driveStore.currentCase.owner.accountId}`);
    }

    function bufferCurrentCase() {
      if (isCasePage.value) {
        bufferCase.value = { ...driveStore.currentCase };
        bufferPhotoList.value = [...driveStore.photoList];
      }
    }

    function returnBufferedCurrentCase() {
      if (isCasePage.value) {
        driveStore.currentCase = { ...bufferCase.value! };
        driveStore.photoList = [...bufferPhotoList.value];
      } else {
        driveStore.resetCurrentCase();
      }
    }
    function getPosition(event) {
      const x = event.offsetX;
      const y = event.clientY;
      positionData.value = { x, y };
    }
    function setItemShared() {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // sharedPopUp.item!.shared = true
    }
    function editCase() {
      driveStore.currentCase.filesAmount = driveStore.currentCase.files.length;
      driveStore.currentCase.photosAmount = driveStore.currentCase.photos.length;
      // hidePopUp();
      router.push(`/drive/case/${driveStore.currentCase.id}/edit`);
    }
    function saveCaseAsCopy() {
      isCaseCopying.value = true;
      isCaseSettingsPopUp.value = false;
      driveStore.saveCaseAsCopy().then((response) => {
        if (response.success) {
          driveStore.currentCase = { ...response.results.data };
          driveStore.photoList = response.results.data.photos;
          caseCopyTitle.value = response.results.data.title;
          isCaseCopying.value = false;
        }
      });
    }

    onMounted(() => {
      bufferCurrentCase();
      fetchCase();
    });

    onUnmounted(() => {
      returnBufferedCurrentCase();
    });

    return {
      driveStore,
      doneAction,
      currentSubpage,
      CASE_VIEW_NAVIGATION,
      caseLoading,
      isCaseSettingsPopUp,
      isMoveToCasePopUp,
      sharedPopUp,
      openMovePopUp,
      deleteCase,
      SHARE_TYPES,
      updateLocation,
      openSharePopUp,
      removeSharedItemFromStore,
      closeSharePopUp,
      setSharedLabels,
      isMy,
      toShare,
      allowSave,
      selectPhoto,
      selectVideo,
      setItemShared,
      saveCaseAsCopy,
      isCaseCopying,
      caseCopyTitle,
      mainStore,
      viewAllCases,
      isNetwork,
      getPosition,
      positionData,
      editCase,
      viewProfile,
      photoUrl,
      fullName,
      createdDate,
      updatedDate,
      viewNetworkCases,
      descriptionWithLink,
    };
  },
});
